import { markRaw, shallowReactive } from 'vue'
import type { ChanelActionType, SubscribeToChannelResult } from '@trading/infrastructure'
import type { OrderBookModel, OrderSide, PositionSide } from '@trading/core'

export type TrendDirection = 'up' | 'down' | 'neutral'

type OrderBookSubscription = {
  consumers: number
  result: SubscribeToChannelResult | null
  promise: Promise<SubscribeToChannelResult | undefined> | null
}

type StoreForDetectDirection = {
  lastPrice: number | null
  lastPriceTime: number | null
  currentPrice: number | null
  direction: TrendDirection | null
}

type OrderBookQuote = {
  bid: number | null
  ask: number | null
  spread: number | null
  updatedAt: number
  direction: TrendDirection
}

const ORDER_BOOK_FLUSH_INTERVAL_MS = 500
const ORDER_BOOK_DETECT_DIRECTION_INTERVAL_MS = 1500

export const useOrderBookStore = defineStore('orderBook', () => {
  const orderBooksBySymbol = shallowReactive<Record<string, OrderBookModel>>({})
  const quotesBySymbol = shallowReactive<Record<string, OrderBookQuote>>({})

  const detectDirectionStore = shallowReactive<Record<string, StoreForDetectDirection>>({})

  const subscriptionsBySymbol = new Map<string, OrderBookSubscription>()

  const orderBookBuffer = new Map<string, OrderBookModel>()
  let flushTimer: ReturnType<typeof setTimeout> | null = null

  function getWsOrderBookService() {
    return useNuxtApp().$wsOrderBookService
  }

  function normalizeSymbol(symbol: string) {
    return symbol ? symbol.trim().toUpperCase() : ''
  }

  function parsePrice(value: string | number | undefined | null): number | null {
    if (value === undefined || value === null) {
      return null
    }

    const parsed = Number.parseFloat(String(value))

    return Number.isFinite(parsed) ? parsed : null
  }

  function extractQuote(orderBook: OrderBookModel): OrderBookQuote {
    const bid = parsePrice(orderBook.bids?.[0]?.price)
    const ask = parsePrice(orderBook.asks?.[0]?.price)

    return {
      bid,
      ask,
      spread: bid !== null && ask !== null ? ask - bid : null,
      updatedAt: Date.now(),
      direction: detectDirection(bid, orderBook.symbol)
    }
  }

  function scheduleFlush() {
    if (flushTimer) {
      return
    }

    flushTimer = setTimeout(() => {
      flushTimer = null
      flushBufferedOrderBooks()
    }, ORDER_BOOK_FLUSH_INTERVAL_MS)
  }

  function queueOrderBookUpdate(symbol: string, orderBook: OrderBookModel) {
    const normalizedSymbol = normalizeSymbol(symbol)

    orderBookBuffer.set(normalizedSymbol, orderBook)
    scheduleFlush()
  }

  function detectDirection(bid: number | null, symbol: string): TrendDirection {
    let direction: TrendDirection = 'neutral'
    if (!detectDirectionStore[symbol]) {
      detectDirectionStore[symbol] = {
        lastPrice: null,
        lastPriceTime: null,
        currentPrice: null,
        direction: null
      }
    }
    const lastPrice = detectDirectionStore[symbol]?.lastPrice
    const lastPriceTime = detectDirectionStore[symbol]?.lastPriceTime
    if (
      lastPriceTime === null ||
      Date.now() - lastPriceTime >= ORDER_BOOK_DETECT_DIRECTION_INTERVAL_MS
    ) {
      if (!bid) {
        detectDirectionStore[symbol].direction = 'neutral'
        return detectDirectionStore[symbol].direction
      }
      if (lastPrice && bid > lastPrice) {
        direction = 'up'
      } else if (lastPrice && bid < lastPrice) {
        direction = 'down'
      } else {
        direction = detectDirectionStore[symbol].direction || 'neutral'
      }
      detectDirectionStore[symbol].lastPrice = bid
      detectDirectionStore[symbol].lastPriceTime = Date.now()
    } else {
      direction = detectDirectionStore[symbol].direction || 'neutral'
    }
    detectDirectionStore[symbol].direction = direction
    return direction
  }

  function flushBufferedOrderBooks() {
    for (const [symbol, orderBook] of orderBookBuffer) {
      // markRaw — чтобы Vue не пытался глубоко трекать большой стакан
      orderBooksBySymbol[symbol] = markRaw(orderBook)

      // легкое состояние для таблицы
      quotesBySymbol[symbol] = extractQuote(orderBook)
    }

    orderBookBuffer.clear()
  }

  function getOrderBook(symbol: string): OrderBookModel | null {
    return orderBooksBySymbol[normalizeSymbol(symbol)] ?? null
  }

  function getQuote(symbol: string): OrderBookQuote | null {
    return quotesBySymbol[normalizeSymbol(symbol)] ?? null
  }

  function getBestAsk(symbol: string): number | null {
    return getQuote(symbol)?.ask ?? null
  }

  function getBestBid(symbol: string): number | null {
    return getQuote(symbol)?.bid ?? null
  }

  function getSpread(symbol: string): number | null {
    return getQuote(symbol)?.spread ?? null
  }

  function getMarketPrice(symbol: string, side: OrderSide): number | null {
    return side.toUpperCase() === 'BUY' ? getBestAsk(symbol) : getBestBid(symbol)
  }

  function getClosePrice(symbol: string, positionSide: PositionSide): number | null {
    return positionSide.toLocaleUpperCase() === 'LONG' ? getBestBid(symbol) : getBestAsk(symbol)
  }

  async function ensureOrderBookSubscription(
    symbol: string,
    actionName: ChanelActionType = 'update'
  ) {
    const normalizedSymbol = normalizeSymbol(symbol)

    let subscription = subscriptionsBySymbol.get(normalizedSymbol)

    if (!subscription) {
      subscription = {
        consumers: 0,
        result: null,
        promise: null
      }

      subscriptionsBySymbol.set(normalizedSymbol, subscription)
    }

    if (subscription.result) {
      return subscription.result
    }

    if (subscription.promise) {
      return subscription.promise
    }

    subscription.promise = getWsOrderBookService()
      .subscribeToOrderBookUpdate(normalizedSymbol, actionName, (data: OrderBookModel) => {
        if (data.symbol && normalizeSymbol(data.symbol) !== normalizedSymbol) {
          return
        }

        queueOrderBookUpdate(normalizedSymbol, data)
      })
      .then((result) => {
        subscription.result = result ?? null
        return result
      })
      .catch((error) => {
        subscriptionsBySymbol.delete(normalizedSymbol)
        throw error
      })
      .finally(() => {
        subscription.promise = null
      })

    return subscription.promise
  }

  async function acquireOrderBook(symbol: string) {
    const normalizedSymbol = normalizeSymbol(symbol)

    let subscription = subscriptionsBySymbol.get(normalizedSymbol)

    if (!subscription) {
      subscription = {
        consumers: 0,
        result: null,
        promise: null
      }

      subscriptionsBySymbol.set(normalizedSymbol, subscription)
    }

    subscription.consumers += 1

    await ensureOrderBookSubscription(normalizedSymbol)

    let released = false

    return () => {
      if (released) {
        return
      }

      released = true
      releaseOrderBook(normalizedSymbol)
    }
  }

  function releaseOrderBook(symbol: string) {
    const normalizedSymbol = normalizeSymbol(symbol)
    const subscription = subscriptionsBySymbol.get(normalizedSymbol)

    if (!subscription) {
      return
    }

    subscription.consumers = Math.max(0, subscription.consumers - 1)

    if (subscription.consumers > 0) {
      return
    }

    subscription.result?.removeHandler?.()
    subscription.result?.unsubscribe?.()

    subscriptionsBySymbol.delete(normalizedSymbol)

    // Не удаляем данные сразу, чтобы UI не мигал
    // delete orderBooksBySymbol[normalizedSymbol]
    // delete quotesBySymbol[normalizedSymbol]
  }

  function resetOrderBook(symbol: string) {
    const normalizedSymbol = normalizeSymbol(symbol)

    orderBookBuffer.delete(normalizedSymbol)

    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete orderBooksBySymbol[normalizedSymbol]

    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete quotesBySymbol[normalizedSymbol]
  }

  function resetAllOrderBooks() {
    orderBookBuffer.clear()

    for (const symbol of Object.keys(orderBooksBySymbol)) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete orderBooksBySymbol[symbol]
    }

    for (const symbol of Object.keys(quotesBySymbol)) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete quotesBySymbol[symbol]
    }
  }
  function releaseAllOrderBooks() {
    for (const symbol of Object.keys(orderBooksBySymbol)) {
      releaseOrderBook(symbol)
    }
  }

  return {
    orderBooksBySymbol,
    quotesBySymbol,

    getOrderBook,
    getQuote,
    getBestAsk,
    getBestBid,
    getSpread,
    getMarketPrice,
    getClosePrice,

    ensureOrderBookSubscription,
    acquireOrderBook,
    releaseOrderBook,
    releaseAllOrderBooks,

    resetOrderBook,
    resetAllOrderBooks,
    detectDirectionStore
  }
})
